/**
 * Generate runs: one Generate press is one run, the jobs that share its `runId`. A job from
 * before runs (or made in the template editor) has none and is a run of its own.
 */
import type { GenerationJobView } from "@genz/platform";
import { ALL_VIEWS } from "../templates/views.js";
import { modelLabel } from "../templates/templateLabels.js";
import { guideMissing } from "./candidates.js";

export interface RunDetails {
  name: string | null;
  productType: string | null;
  brand: string | null;
  model: string | null;
  phoneModelId: string | null;
  caseStyleId: string | null;
  baseType: string | null;
}

/** One Generate run: its jobs in view order, and the Details they share. */
export interface Run {
  runId: string;
  jobs: GenerationJobView[];
  details: RunDetails;
}

export interface RunCounts {
  review: number;
  generating: number;
  ready: number;
  failed: number;
}

/** A Mock-up only run: no review step, no print guide, no template. */
export const mockupOnlyRun = (run: Run) => run.jobs.every((j) => j.mockupOnly);

/** A regenerated job without a run joins the run named by its old job's id. */
export const runKey = (job: GenerationJobView) => job.runId || job.id;

const viewRank = (view: string) => {
  const at = (ALL_VIEWS as readonly string[]).indexOf(view);
  return at < 0 ? ALL_VIEWS.length : at;
};

const createdAt = (job: GenerationJobView) => job.createdAt ?? "";

const newest = (run: Run) => run.jobs.reduce((t, j) => (createdAt(j) > t ? createdAt(j) : t), "");

function detailsOf(job: GenerationJobView): RunDetails {
  return {
    name: job.name ?? null, productType: job.productType ?? null, brand: job.brand ?? null,
    model: job.model ?? null, phoneModelId: job.phoneModelId ?? null,
    caseStyleId: job.caseStyleId ?? null, baseType: job.baseType ?? null,
  };
}

/** Jobs grouped by run, newest run first. */
export function groupRuns(jobs: GenerationJobView[]): Run[] {
  const byRun = new Map<string, GenerationJobView[]>();
  for (const job of jobs) byRun.set(runKey(job), [...(byRun.get(runKey(job)) ?? []), job]);
  const runs = [...byRun].map(([runId, list]) => {
    const sorted = [...list].sort((a, b) => viewRank(a.view) - viewRank(b.view));
    return { runId, jobs: sorted, details: detailsOf(sorted[0]) };
  });
  return runs.sort((a, b) => newest(b).localeCompare(newest(a)));
}

/** The newest job of each view, in view order: older images of a view are kept, not shown. */
export function latestPerView(run: Run): GenerationJobView[] {
  const byView = new Map<string, GenerationJobView>();
  for (const job of run.jobs) {
    const kept = byView.get(job.view);
    if (!kept || createdAt(job) > createdAt(kept)) byView.set(job.view, job);
  }
  return [...byView.values()];
}

/** The run's template: the one its jobs were published as; the newest job's when they differ. */
export function runTemplateId(run: Run): string | undefined {
  let found: GenerationJobView | undefined;
  for (const job of run.jobs) {
    if (job.publishedTemplateId && (!found || createdAt(job) > createdAt(found))) found = job;
  }
  return found?.publishedTemplateId ?? undefined;
}

/** A held job waits on the cloud's recheck but is shown failed, as its row shows it; so is a
 *  finished job whose print guide could not be made. */
function stateOf(job: GenerationJobView): keyof RunCounts | null {
  if (guideMissing(job)) return "failed";
  switch (job.status) {
    case "clear_review": return "review";
    case "queued":
    case "processing": return "generating";
    case "completed":
    case "needs_review":
    case "approved": return "ready";
    case "failed":
    case "held": return "failed";
    default: return null;
  }
}

export function runCounts(run: Run): RunCounts {
  const counts: RunCounts = { review: 0, generating: 0, ready: 0, failed: 0 };
  for (const job of latestPerView(run)) {
    const state = stateOf(job);
    if (state) counts[state] += 1;
  }
  return counts;
}

/** "3 to review · 2 generating · 1 ready · 1 failed", naming only the states a view is in. */
export function runCountsText(counts: RunCounts): string {
  return [
    [counts.review, "to review"], [counts.generating, "generating"], [counts.ready, "ready"], [counts.failed, "failed"],
  ].filter(([n]) => n).map(([n, words]) => `${n} ${words}`).join(" · ");
}

/** The phone's name, with the brand once even when the catalog's model name repeats it. */
export function phoneName(brand?: string | null, model?: string | null): string {
  return model?.trim() ? modelLabel({ brand, model }) : (brand ?? "").trim();
}

export const viewLabel = (view: string) => view.replace(/\b\w/g, (c) => c.toUpperCase());
